export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export const v3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });
export const add = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
export const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
export const scale = (a: Vec3, s: number): Vec3 => ({ x: a.x * s, y: a.y * s, z: a.z * s });
export const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;
export const len = (a: Vec3): number => Math.sqrt(dot(a, a));
export const dist = (a: Vec3, b: Vec3): number => len(sub(a, b));
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
export const lerpV = (a: Vec3, b: Vec3, t: number): Vec3 => ({
  x: lerp(a.x, b.x, t),
  y: lerp(a.y, b.y, t),
  z: lerp(a.z, b.z, t),
});
export const norm = (a: Vec3): Vec3 => {
  const l = len(a);
  return l > 1e-9 ? scale(a, 1 / l) : v3();
};
export const clamp = (x: number, lo: number, hi: number): number => (x < lo ? lo : x > hi ? hi : x);

export const TAU = Math.PI * 2;
export const wrapAngle = (a: number): number => {
  a = a % TAU;
  if (a > Math.PI) a -= TAU;
  if (a < -Math.PI) a += TAU;
  return a;
};
export const lerpAngle = (a: number, b: number, t: number): number => a + wrapAngle(b - a) * t;

/**
 * View direction for yaw/pitch. Convention (right-handed, Y up): yaw 0 looks down -Z,
 * positive yaw turns left (counter-clockwise seen from above), positive pitch looks up.
 */
export const viewDir = (yaw: number, pitch: number): Vec3 => {
  const cp = Math.cos(pitch);
  return { x: -Math.sin(yaw) * cp, y: Math.sin(pitch), z: -Math.cos(yaw) * cp };
};
/** Horizontal forward/right basis for movement. */
export const flatBasis = (yaw: number) => ({
  fwd: { x: -Math.sin(yaw), y: 0, z: -Math.cos(yaw) },
  right: { x: Math.cos(yaw), y: 0, z: -Math.sin(yaw) },
});

/** Deterministic PRNG (mulberry32) so spread patterns can be predicted on the client. */
export function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Closest distance between segment AB and point P; returns t along AB too. */
export function closestOnSegment(a: Vec3, b: Vec3, p: Vec3): { t: number; point: Vec3 } {
  const ab = sub(b, a);
  const l2 = dot(ab, ab);
  const t = l2 > 0 ? clamp(dot(sub(p, a), ab) / l2, 0, 1) : 0;
  return { t, point: add(a, scale(ab, t)) };
}

/**
 * Ray vs capsule (segment p0-p1, radius r). Returns distance along the (normalized) ray
 * or -1. Implemented as ray vs swept sphere via sampling-free analytic approach.
 */
export function rayCapsule(origin: Vec3, dir: Vec3, p0: Vec3, p1: Vec3, r: number, maxDist: number): number {
  // Based on Inigo Quilez's capsule intersection.
  const ba = sub(p1, p0);
  const oa = sub(origin, p0);
  const baba = dot(ba, ba);
  const bard = dot(ba, dir);
  const baoa = dot(ba, oa);
  const rdoa = dot(dir, oa);
  const oaoa = dot(oa, oa);
  const a = baba - bard * bard;
  let b = baba * rdoa - baoa * bard;
  let c = baba * oaoa - baoa * baoa - r * r * baba;
  let h = b * b - a * c;
  if (h >= 0 && a > 1e-9) {
    const t = (-b - Math.sqrt(h)) / a;
    const y = baoa + t * bard;
    if (y > 0 && y < baba && t >= 0 && t <= maxDist) return t;
    // caps
    const oc = y <= 0 ? oa : sub(origin, p1);
    b = dot(dir, oc);
    c = dot(oc, oc) - r * r;
    h = b * b - c;
    if (h > 0) {
      const t2 = -b - Math.sqrt(h);
      if (t2 >= 0 && t2 <= maxDist) return t2;
    }
    return -1;
  }
  // Ray parallel to capsule axis: test both caps as spheres.
  let best = -1;
  for (const cp of [p0, p1]) {
    const oc = sub(origin, cp);
    const bb = dot(dir, oc);
    const cc = dot(oc, oc) - r * r;
    const hh = bb * bb - cc;
    if (hh > 0) {
      const t = -bb - Math.sqrt(hh);
      if (t >= 0 && t <= maxDist && (best < 0 || t < best)) best = t;
    }
  }
  return best;
}

/** Sphere (center c, radius r) vs capsule overlap. */
export function sphereCapsule(c: Vec3, r: number, p0: Vec3, p1: Vec3, cr: number): boolean {
  const { point } = closestOnSegment(p0, p1, c);
  return dist(point, c) <= r + cr;
}
